import React from "react";
import { Link } from "react-router-dom";
import LegalLayout, { Section } from "./LegalLayout";

export default function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" updated="February 15, 2026">
      <p>
        Welcome to LifeGuide. These Terms of Service ("Terms") govern your use
        of the LifeGuide website, mobile web app, and related services
        (collectively, the "Service"). By creating an account, accessing, or
        using the Service, you agree to these Terms.
      </p>

      <Section heading="1. Eligibility">
        <p>
          You must be at least 13 years old to use LifeGuide, and at least 18
          years old to purchase a subscription or gift. By using the Service
          you represent you meet these requirements.
        </p>
      </Section>

      <Section heading="2. Accounts">
        <p>
          You are responsible for the accuracy of the information you provide
          and for safeguarding your password. You are responsible for all
          activity that occurs under your account. Notify us promptly of any
          unauthorized use.
        </p>
      </Section>

      <Section heading="3. Subscriptions, Trials, and Gifts">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Free trial.</strong> New accounts may receive a 3-day free Pro trial without a credit card. If you do not subscribe, your account will automatically revert to the free tier at trial end.</li>
          <li><strong>Paid subscription.</strong> Pro subscriptions renew monthly at USD $3.99 (or the price shown at checkout) until you cancel. Payments are processed by Stripe.</li>
          <li><strong>Cancellation.</strong> You may cancel at any time from your Profile page. Access continues until the end of the current billing period; no partial refunds.</li>
          <li><strong>Gifts.</strong> Gift purchases are single, non-recurring transactions for 1, 3, or 12 months of Pro access. Gifts are non-refundable once the redemption link has been delivered.</li>
          <li><strong>Taxes.</strong> Prices exclude any applicable taxes, which will be added at checkout where required.</li>
        </ul>
      </Section>

      <Section heading="4. SMS / Text Messaging Terms">
        <p>
          LifeGuide may offer optional SMS/text messaging features, including
          account notifications, reminders, gift notifications, inspirational
          or reflection messages, and other messages related to a user's use
          of LifeGuide.
        </p>

        <p><strong>Informational / Service SMS Only.</strong></p>
        <p>
          LifeGuide's SMS program is limited to informational and service
          messages related to a user's use of LifeGuide. LifeGuide does
          <strong> not</strong> send marketing or promotional SMS/text
          messages under this program, and no LifeGuide user is asked to
          consent to marketing/promotional SMS as part of any LifeGuide
          checkbox, purchase, or account flow. Marketing/promotional SMS
          consent, if ever offered, would be collected through a separate
          affirmative opt-in that is not combined with any other consent.
        </p>

        <p><strong>Consent to Receive Text Messages.</strong></p>
        <p>
          LifeGuide will send recurring SMS/text messages only to users who
          have provided their mobile phone number and affirmatively opted in
          to receive such messages. Providing a phone number alone does not
          constitute consent to receive recurring SMS messages.
        </p>
        <p>
          By checking the SMS consent box or otherwise expressly opting in,
          the user agrees to receive recurring automated SMS/text messages
          from LifeGuide at the mobile number provided.
        </p>
        <p>
          Consent to receive SMS/text messages is not a condition of
          purchasing any product, subscription, gift, or service.
        </p>

        <p><strong>Message Frequency and Charges.</strong></p>
        <p>
          Message frequency may vary based on the user's preferences and use
          of LifeGuide. Message and data rates may apply according to the
          user's wireless carrier and mobile plan.
        </p>

        <p><strong>Opting Out.</strong></p>
        <p>
          Users may opt out of SMS/text messages at any time by replying{" "}
          <strong>STOP</strong> to a LifeGuide text message. After sending
          STOP, the user may receive a confirmation message indicating that
          they have been unsubscribed. No additional recurring SMS messages
          will be sent unless the user opts in again.
        </p>
        <p>
          For assistance, users may reply <strong>HELP</strong> to a
          LifeGuide text message or contact LifeGuide through the support
          information provided on the LifeGuide website.
        </p>

        <p><strong>Gifts and Messages to Other People.</strong></p>
        <p>
          Purchasing or sending a LifeGuide gift does not automatically
          enroll the gift recipient in recurring SMS/text messages.
        </p>
        <p>
          A gift purchaser may provide a recipient's mobile number for
          purposes of delivering a requested gift notification or invitation
          where permitted. The recipient must separately and affirmatively
          opt in before LifeGuide sends recurring reminder, inspirational,
          reflection, or similar informational/service SMS/text messages.
        </p>
        <p>
          A purchaser or other user cannot provide consent to recurring
          SMS/text messages on behalf of another person.
        </p>

        <p><strong>Privacy and Mobile Information.</strong></p>
        <p>
          LifeGuide handles personal information in accordance with its{" "}
          <Link to="/privacy" className="text-[#4F6F52] underline underline-offset-4">
            Privacy Policy
          </Link>
          .
        </p>
        <p>
          Mobile phone numbers, SMS consent information, and opt-in data
          will not be sold or shared with third parties or affiliates for
          their own marketing or promotional purposes.
        </p>
        <p>
          SMS opt-in data and consent will not be shared with third parties
          for purposes unrelated to providing the messaging service, except
          as necessary for service providers that assist LifeGuide in
          delivering messages or as required by law.
        </p>

        <p><strong>Carrier Disclaimer.</strong></p>
        <p>
          Wireless carriers are not responsible for delayed or undelivered
          messages. SMS/text message delivery may be affected by the user's
          wireless carrier, device, network availability, and other factors
          outside LifeGuide's control.
        </p>

        <p><strong>Eligibility.</strong></p>
        <p>
          Users must be at least 18 years old or otherwise legally
          authorized to provide consent for SMS/text messaging. By opting
          in, a user represents that they are the authorized user of the
          mobile number provided or have permission to use that number for
          this purpose.
        </p>
        <p>
          Questions about our SMS communications? Email{" "}
          <a href="mailto:support@trylifeguide.com" className="text-[#4F6F52] underline underline-offset-4">support@trylifeguide.com</a>.
        </p>
      </Section>

      <Section heading="5. Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Use the Service to violate any law or another person's rights.</li>
          <li>Submit content that is unlawful, harmful, hateful, sexually explicit, or that promotes self-harm or violence.</li>
          <li>Attempt to reverse-engineer, scrape, or interfere with the Service.</li>
          <li>Resell, sublicense, or use the Service to build a competing product.</li>
          <li>Use the Service to send unsolicited messages to any person.</li>
        </ul>
      </Section>

      <Section heading="6. AI-Generated Content and Not Professional Advice">
        <p>
          LifeGuide provides devotionals, reflections, coaching responses, and
          audio generated in part by artificial intelligence. Content is for
          personal, spiritual, and reflective purposes only and is
          <strong> not </strong>
          a substitute for professional medical, mental-health, legal, or
          financial advice. If you are experiencing a crisis or thoughts of
          self-harm, contact a qualified professional or, in the United States,
          call or text 988 (Suicide & Crisis Lifeline).
        </p>
      </Section>

      <Section heading="7. Your Content">
        <p>
          You retain ownership of the content you submit to LifeGuide (journal
          entries, chat messages, texts you analyze, and reflections). You
          grant LifeGuide a limited, non-exclusive, worldwide license to
          process, store, and display that content solely to operate the
          Service for you.
        </p>
      </Section>

      <Section heading="8. Intellectual Property">
        <p>
          LifeGuide, our logos, and all Service materials other than your
          content are owned by LifeGuide or its licensors and are protected by
          copyright, trademark, and other laws. Scripture quotations may be
          drawn from public-domain translations or licensed sources; all
          scripture rights remain with the original publishers.
        </p>
      </Section>

      <Section heading="9. Third-Party Services">
        <p>
          The Service integrates with third-party providers (Stripe, Twilio,
          Resend, OpenAI, Anthropic, MongoDB Atlas). Your use of those
          providers may be subject to their own terms and privacy policies. We
          are not responsible for their acts or omissions.
        </p>
      </Section>

      <Section heading="10. Termination">
        <p>
          You may close your account at any time. We may suspend or terminate
          your access if you violate these Terms or use the Service in a way
          that harms us, other users, or third parties. Sections that by their
          nature should survive termination will survive.
        </p>
      </Section>

      <Section heading="11. Disclaimer of Warranties">
        <p>
          The Service is provided "as is" and "as available." To the maximum
          extent permitted by law, LifeGuide disclaims all warranties, express
          or implied, including merchantability, fitness for a particular
          purpose, and non-infringement.
        </p>
      </Section>

      <Section heading="12. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, LifeGuide's total liability
          for any claim arising out of or relating to the Service will not
          exceed the greater of (a) the amount you paid LifeGuide in the 12
          months before the claim, or (b) USD $50. LifeGuide will not be liable
          for indirect, incidental, consequential, special, or punitive
          damages.
        </p>
      </Section>

      <Section heading="13. Indemnification">
        <p>
          You agree to indemnify and hold harmless LifeGuide from any claim,
          loss, or expense (including reasonable attorneys' fees) arising out
          of your content, your use of the Service, or your violation of these
          Terms.
        </p>
      </Section>

      <Section heading="14. Governing Law and Disputes">
        <p>
          These Terms are governed by the laws of the State of Delaware,
          without regard to conflict-of-laws principles. Any dispute will be
          resolved exclusively in the state or federal courts located in
          Delaware, and you consent to their jurisdiction.
        </p>
      </Section>

      <Section heading="15. Changes to These Terms">
        <p>
          We may update these Terms from time to time. Material changes will be
          announced by email or in-app notice at least 7 days before they take
          effect. Continued use after that date constitutes acceptance.
        </p>
      </Section>

      <Section heading="16. Contact">
        <p>
          Questions about these Terms?{" "}
          <Link to="/contact" className="text-[#4F6F52] underline underline-offset-4" data-testid="terms-contact-link">Send us a message</Link>{" "}
          or email <a href="mailto:support@lifeguide.app" className="text-[#4F6F52] underline underline-offset-4">support@lifeguide.app</a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
